
import { supabase } from '@/lib/supabase/client';
import { executeWithTimeout } from '@/lib/supabase';

/**
 * Create user role via RPC
 * @param userId User ID
 * @returns Success status
 */
export const createUserRoleViaRPC = async (userId: string) => {
  console.log('[RoleCreationService] Creating role via RPC for user:', userId);
  
  try {
    // Attempt to call the RPC function
    const { data, error } = await supabase.rpc('create_user_role', { 
      user_id_input: userId,
      role_input: 'USER'
    });
    
    if (error) {
      if (error.message.includes('function') && error.message.includes('not found')) {
        console.log('[RoleCreationService] RPC function not found, need to use fallback');
        return false;
      }
      
      console.error('[RoleCreationService] RPC error:', error);
      return false;
    }
    
    console.log('[RoleCreationService] Role created successfully via RPC');
    return true;
  } catch (error) {
    console.error('[RoleCreationService] Exception in createUserRoleViaRPC:', error);
    return false;
  }
};

/**
 * Insert user role directly
 * @param userId User ID
 * @returns Success status
 */
export const insertUserRoleDirectly = async (userId: string) => {
  console.log('[RoleCreationService] Inserting role directly for user:', userId);
  
  try {
    // Try first with the user_roles table that has a 'role' text column
    const { error: textRoleError } = await executeWithTimeout(
      async () => await supabase
        .from('user_roles')
        .insert([{ user_id: userId, role: 'USER' }]),
      5000
    );
    
    if (!textRoleError) {
      console.log('[RoleCreationService] Role created with text field');
      return true;
    }
    
    console.log('[RoleCreationService] Text field insertion failed, trying with role_id field');
    
    // If that fails, try with the user_roles table that has a role_id FK column
    // First we need to find the role_id for 'USER'
    const { data: roleData, error: roleError } = await supabase
      .from('roles')
      .select('id')
      .eq('name', 'USER')
      .single();
    
    if (roleError || !roleData) {
      console.error('[RoleCreationService] Failed to find USER role ID:', roleError);
      
      // Final fallback: try to insert with a hardcoded role ID or name
      const { error: fallbackError } = await executeWithTimeout(
        async () => await supabase
          .from('user_roles')
          .insert([{ 
            user_id: userId, 
            role_id: roleData?.id || '00000000-0000-0000-0000-000000000001',
            role: 'USER'
          }]),
        5000
      );
      
      if (fallbackError) {
        console.error('[RoleCreationService] Fallback insertion failed:', fallbackError);
        return false;
      }
      
      console.log('[RoleCreationService] Role created with fallback method');
      return true;
    }
    
    // Insert using the found role_id
    const { error: fkRoleError } = await executeWithTimeout(
      async () => await supabase
        .from('user_roles')
        .insert([{ user_id: userId, role_id: roleData.id }]),
      5000
    );
    
    if (fkRoleError) {
      console.error('[RoleCreationService] FK role insertion failed:', fkRoleError);
      return false;
    }
    
    console.log('[RoleCreationService] Role created with role_id field');
    return true;
  } catch (error) {
    console.error('[RoleCreationService] Exception in insertUserRoleDirectly:', error);
    return false;
  }
};
